import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { toast } from "sonner";

// Editor Documento clinico avanzato (Professionista/Struttura). contentEditable + toolbar (grassetto/corsivo/elenchi),
// intestazione, logo (profilo o caricato), firma, piè di pagina, modelli. Sanitizzazione server-side (bleach allowlist).
// §2: nuovo documento vuoto -> NIENTE precompilazione. Gli elementi compaiono solo caricando un modello o modificando
// un documento esistente, oppure se l'utente sceglie di aggiungerli manualmente.
// ctx: { folderId } | { episode:{...} }. editDoc: { fid, id } per modifica.
export function ClinicalDocEditor({ ctx = {}, editDoc, onClose, onSaved, embedded }) {
  const [title, setTitle] = useState(ctx.titleHint || "");
  const [header, setHeader] = useState("");
  const [signature, setSignature] = useState("");
  const [footer, setFooter] = useState("");
  const [logoMode, setLogoMode] = useState("none"); // none | profile | custom
  const [logoData, setLogoData] = useState(null);    // data URL (custom)
  const [showMeta, setShowMeta] = useState(false);   // pannello intestazione/logo/firma/piè
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!editDoc);
  const [templates, setTemplates] = useState([]);
  const bodyRef = useRef(null);

  const exec = (cmd) => { document.execCommand(cmd, false, null); bodyRef.current?.focus(); };

  useEffect(() => {
    api.get("/clinical-templates").then(({ data }) => setTemplates(data)).catch(() => {});
    if (editDoc) {
      // §6/7: carica INTEGRALMENTE l'ultima revisione del documento/bozza (mai contenuto vuoto)
      api.get(`/exams/${editDoc.fid}/clinical-documents/${editDoc.id}`)
        .then(({ data }) => {
          setTitle(data.title || ""); setHeader(data.header || "");
          setSignature(data.signature || ""); setFooter(data.footer || "");
          setLogoMode(data.logo_mode || "none");
          setLogoData(data.logo_mode === "custom" ? (data.logo_url || null) : null);
          if (bodyRef.current) bodyRef.current.innerHTML = data.content_format === "html" ? (data.content || "") : (data.content || "").replace(/\n/g, "<br>");
          if (data.header || data.signature || data.footer || (data.logo_mode && data.logo_mode !== "none")) setShowMeta(true);
        })
        .catch(() => toast.error("Impossibile caricare il documento"))
        .finally(() => setLoading(false));
    }
    // §2: nuovo documento -> NON precaricare alcun modello predefinito.
  }, []); // eslint-disable-line

  const applyTemplate = (tid) => {
    const tpl = templates.find((x) => x.id === tid); if (!tpl) return;
    // §3: il modello precarica logo + intestazione + contenuto + firma + piè
    setHeader(tpl.header || ""); setSignature(tpl.signature || ""); setFooter(tpl.footer || "");
    setLogoMode(tpl.logo_mode || "none");
    setLogoData(tpl.logo_mode === "custom" ? (tpl.logo_url || null) : null);
    if (bodyRef.current) bodyRef.current.innerHTML = tpl.content || "";
    setShowMeta(true);
    toast.success("Modello caricato");
  };

  const onLogoFile = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) return toast.error("Logo: usa PNG, JPEG o WEBP");
    if (file.size > 512 * 1024) return toast.error("Logo troppo grande (max 512KB)");
    const r = new FileReader();
    r.onload = () => { setLogoData(r.result); setLogoMode("custom"); };
    r.readAsDataURL(file);
  };

  const metaPayload = () => ({
    header, signature, footer,
    logo_mode: logoMode,
    logo_url: logoMode === "custom" ? logoData : null,
  });

  const payload = (publish) => ({
    ...(editDoc ? {} : ctx.folderId ? { folder_id: ctx.folderId } : { ...(ctx.episode || {}) }),
    title, content: bodyRef.current?.innerHTML || "", content_format: "html", ...metaPayload(), publish,
  });

  const save = async (publish) => {
    if (!title.trim()) return toast.error("Inserisci un titolo");
    setBusy(true);
    try {
      if (editDoc) await api.put(`/exams/${editDoc.fid}/clinical-documents/${editDoc.id}`, payload(publish));
      else await api.post("/clinical-documents", payload(publish));
      toast.success(publish ? "Documento clinico pubblicato" : "Bozza salvata");
      onSaved?.(); onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "no_relationship" ? "Nessuna relazione clinica con il paziente"
        : d === "org_not_clinical" ? "Struttura senza attività clinica"
        : d === "invalid_logo" || d === "logo_too_large" ? "Logo non valido (PNG/JPEG/WEBP, max 512KB)"
        : d === "not_authorized" || d === "author_only" ? "Accesso non consentito" : "Errore durante il salvataggio");
    } finally { setBusy(false); }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Nome del modello:");
    if (!name) return;
    try {
      // §4: salva nome + logo + intestazione + contenuto + firma + piè
      await api.post("/clinical-templates", { name, content: bodyRef.current?.innerHTML || "", content_format: "html", ...metaPayload() });
      const { data } = await api.get("/clinical-templates"); setTemplates(data);
      toast.success("Modello salvato");
    } catch { toast.error("Errore salvataggio modello"); }
  };

  const setDefault = async (tid) => {
    try { await api.post(`/clinical-templates/${tid}/default`); const { data } = await api.get("/clinical-templates"); setTemplates(data); toast.success("Modello predefinito impostato"); }
    catch { toast.error("Errore"); }
  };

  const body = (
    <div className="bg-white rounded-xl w-full max-w-2xl p-5 space-y-3 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="clinical-doc-editor">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">{editDoc ? "Modifica documento clinico" : "Redigi documento clinico"}</h3>
        <button onClick={onClose} data-testid="clinical-doc-editor-close" aria-label="Chiudi">✕</button>
      </div>
      {loading ? <div className="text-sm text-slate-400">Caricamento…</div> : (<>
        <div className="flex items-center gap-2 flex-wrap">
          {templates.length > 0 && (
            <select className="yp-input !py-1 text-sm" onChange={(e) => e.target.value && applyTemplate(e.target.value)} defaultValue="" data-testid="clinical-doc-template-select">
              <option value="">Carica modello…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " ★" : ""}</option>)}
            </select>)}
          <button type="button" className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs" onClick={saveAsTemplate} data-testid="clinical-doc-save-template">Salva come modello</button>
          {templates.filter((t) => !t.is_default).map((t) => (
            <button key={t.id} type="button" className="text-[10px] text-slate-400 underline" onClick={() => setDefault(t.id)} data-testid="clinical-doc-set-default">predefinito: {t.name}</button>))}
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Titolo documento</label>
          <input className="yp-input w-full" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Relazione clinica — 27/09/2026" data-testid="clinical-doc-title" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Contenuto</label>
          <div className="flex gap-1 mb-1" data-testid="clinical-doc-toolbar">
            <button type="button" className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-xs font-bold" onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} data-testid="clinical-doc-bold">B</button>
            <button type="button" className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-xs italic" onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} data-testid="clinical-doc-italic">I</button>
            <button type="button" className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-xs" onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} data-testid="clinical-doc-ul">• Elenco</button>
            <button type="button" className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-xs" onMouseDown={(e) => { e.preventDefault(); exec("insertOrderedList"); }} data-testid="clinical-doc-ol">1. Elenco</button>
          </div>
          <div ref={bodyRef} contentEditable suppressContentEditableWarning
            className="yp-input w-full min-h-[200px] overflow-auto [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-6 [&_ol]:ml-6 [&_li]:my-0.5"
            style={{ whiteSpace: "pre-wrap" }} data-testid="clinical-doc-content" />
        </div>

        {!showMeta && (
          <button type="button" className="text-xs text-blue-600 underline" onClick={() => setShowMeta(true)} data-testid="clinical-doc-add-meta">
            + Aggiungi intestazione, logo, firma e piè di pagina
          </button>)}

        {showMeta && (<div className="space-y-3 border-t border-slate-100 pt-3" data-testid="clinical-doc-meta">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Intestazione</label>
            <textarea className="yp-input w-full min-h-[60px]" value={header} onChange={(e) => setHeader(e.target.value)} placeholder="Es. Studio / Struttura, indirizzo, contatti" data-testid="clinical-doc-header" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Logo</label>
            <div className="flex items-center gap-3 flex-wrap text-sm" data-testid="clinical-doc-logo">
              <label className="flex items-center gap-1"><input type="radio" name="logomode" checked={logoMode === "none"} onChange={() => { setLogoMode("none"); setLogoData(null); }} data-testid="clinical-doc-logo-none" />Nessuno</label>
              <label className="flex items-center gap-1"><input type="radio" name="logomode" checked={logoMode === "profile"} onChange={() => { setLogoMode("profile"); setLogoData(null); }} data-testid="clinical-doc-logo-profile" />Logo del profilo</label>
              <label className="flex items-center gap-1"><input type="radio" name="logomode" checked={logoMode === "custom"} onChange={() => setLogoMode("custom")} data-testid="clinical-doc-logo-custom" />Logo del modello</label>
              {logoMode === "custom" && (
                <label className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs cursor-pointer" data-testid="clinical-doc-logo-upload">
                  Carica logo…
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { onLogoFile(e.target.files[0]); e.target.value = ""; }} />
                </label>)}
              {logoMode === "custom" && logoData && <img src={logoData} alt="logo" className="w-8 h-8 rounded object-cover" data-testid="clinical-doc-logo-preview" />}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500 block mb-1">Firma</label>
              <textarea className="yp-input w-full min-h-[70px]" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Dott. Nome Cognome / Struttura" data-testid="clinical-doc-signature" /></div>
            <div><label className="text-xs text-slate-500 block mb-1">Piè di pagina</label>
              <textarea className="yp-input w-full min-h-[70px]" value={footer} onChange={(e) => setFooter(e.target.value)} placeholder="Dati struttura, contatti, note" data-testid="clinical-doc-footer" /></div>
          </div>
        </div>)}

        <div className="flex justify-end gap-2 flex-wrap">
          <button className="yp-btn yp-btn-ghost" onClick={onClose} disabled={busy} data-testid="clinical-doc-cancel">Annulla</button>
          <button className="yp-btn yp-btn-outline" onClick={() => save(false)} disabled={busy} data-testid="clinical-doc-save-draft">Salva bozza</button>
          <button className="yp-btn yp-btn-primary" onClick={() => save(true)} disabled={busy} data-testid="clinical-doc-publish">Pubblica</button>
        </div>
      </>)}
    </div>
  );

  if (embedded) return body;
  return <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="clinical-doc-editor-overlay">{body}</div>;
}
